import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Loader from './Loader';
import Message from './Message';
import { useGetCustomerActivitiesQuery, useAddCustomerActivityMutation,
  useUpdateCustomerActivityMutation, useDeleteCustomerActivityMutation } from '../slices/customerActivitiesApiSlice';

const types = { phone: 'Phone Call', visit: 'In-Person Visit', email: 'Email', message: 'Text / Message', other: 'Other' };
const localDateTime = (value = new Date()) => {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const emptyForm = () => ({ interactionAt: localDateTime(), interactionType: 'phone', notes: '', nextFollowUpAt: '' });
const formatTime = (value) => new Date(value).toLocaleString();
// Follow-up is a calendar date, so do not shift it into the viewer's timezone.
const formatFollowUp = (value) => new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' });

const CustomerActivities = ({ customerId }) => {
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const lock = useRef(false);
  const formRef = useRef(null);
  const { currentData: data, isFetching, error, refetch } = useGetCustomerActivitiesQuery(
    { customerId, page }, { refetchOnMountOrArgChange: true });
  const [addActivity, { isLoading: adding }] = useAddCustomerActivityMutation();
  const [updateActivity, { isLoading: updating }] = useUpdateCustomerActivityMutation();
  const [deleteActivity, { isLoading: deleting }] = useDeleteCustomerActivityMutation();
  const busy = adding || updating || deleting;

  useEffect(() => {
    if (data && page > data.pages) setPage(data.pages);
  }, [data, page]);

  const reset = () => { setForm(emptyForm()); setEditingId(null); };
  const change = (event) => setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  const save = async (event) => {
    event.preventDefault();
    if (lock.current) return;
    if (!form.notes.trim() || form.notes.length > 5000 || !Number.isFinite(Date.parse(form.interactionAt))) {
      toast.error('Enter a valid interaction date and notes (maximum 5,000 characters).');
      return;
    }
    lock.current = true;
    try {
      const payload = { ...form, customerId, interactionAt: new Date(form.interactionAt).toISOString() };
      if (editingId) await updateActivity({ ...payload, activityId: editingId }).unwrap();
      else await addActivity(payload).unwrap();
      toast.success(editingId ? 'Customer activity updated.' : 'Customer activity added.');
      reset();
      setPage(1);
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Unable to save customer activity.');
    } finally { lock.current = false; }
  };

  const edit = (activity) => {
    setEditingId(activity._id);
    setForm({ interactionAt: localDateTime(activity.interactionAt), interactionType: activity.interactionType,
      notes: activity.notes, nextFollowUpAt: activity.nextFollowUpAt?.slice(0, 10) || '' });
    formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  };
  const remove = async (activityId) => {
    if (lock.current || !window.confirm('Are you sure you want to delete this customer activity?')) return;
    lock.current = true;
    try {
      await deleteActivity({ customerId, activityId }).unwrap();
      if (editingId === activityId) reset();
      toast.success('Customer activity deleted.');
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Unable to delete customer activity.');
    } finally { lock.current = false; }
  };

  return (
    <Card className="border-0 shadow-sm mt-4">
      <Card.Header className="bg-white border-bottom fw-semibold py-3">Customer Activity &amp; Sales Notes</Card.Header>
      <Card.Body>
        <Form onSubmit={save} ref={formRef}>
          {editingId && <h2 className="h6">Edit Activity</h2>}
          <fieldset disabled={busy}>
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group controlId="activityInteractionAt">
                  <Form.Label>Interaction Date</Form.Label>
                  <Form.Control type="datetime-local" name="interactionAt" value={form.interactionAt} onChange={change} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="activityType">
                  <Form.Label>Activity Type</Form.Label>
                  <Form.Select name="interactionType" value={form.interactionType} onChange={change}>
                    {Object.entries(types).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="activityFollowUp">
                  <Form.Label>Next Follow-up Date</Form.Label>
                  <Form.Control type="date" name="nextFollowUpAt" value={form.nextFollowUpAt} onChange={change} />
                  <Form.Text className="text-muted">Optional</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="activityNotes" className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={4} name="notes" value={form.notes} onChange={change} required maxLength={5000} />
              <Form.Text className="text-muted">{form.notes.length} / 5,000 characters</Form.Text>
            </Form.Group>
            <Button type="submit" variant="primary">{adding || updating ? 'Saving…' : editingId ? 'Save Changes' : 'Add Activity'}</Button>
            {editingId && <Button type="button" variant="outline-secondary" className="ms-2" onClick={reset}>Cancel</Button>}
          </fieldset>
        </Form>
        <hr />
        <h2 className="h6">Activity History</h2>
        {error ? <Message variant="danger">
          {error?.data?.message || error?.error || 'Unable to load customer activities.'}{' '}
          <Button variant="outline-secondary" size="sm" onClick={refetch}>Retry</Button>
        </Message> : isFetching ? <Loader /> : data && <>
          {data.activities.length === 0 && <p className="text-muted">No customer activity has been recorded yet.</p>}
          {data.activities.map((activity) => (
            <div key={activity._id} className="border-bottom py-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <strong>{formatTime(activity.interactionAt)} — {types[activity.interactionType]}</strong>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => edit(activity)}>Edit</Button>
                  <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => remove(activity._id)}>Delete</Button>
                </div>
              </div>
              <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{activity.notes}</p>
              <div className="small text-muted">Added by: {activity.createdByName || activity.createdBy?.name || 'Former staff member'}</div>
              <div className="small text-muted">Recorded: {formatTime(activity.createdAt)}</div>
              {activity.updatedAt !== activity.createdAt && <div className="small text-muted">Updated: {formatTime(activity.updatedAt)}</div>}
              {activity.nextFollowUpAt && <div className="small mt-2">Next Follow-up Date: {formatFollowUp(activity.nextFollowUpAt)}</div>}
            </div>
          ))}
          {data.pages > 1 && <div className="d-flex align-items-center gap-3 mt-3">
            <Button size="sm" variant="outline-secondary" disabled={busy || page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <span className="small">Page {page} of {data.pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={busy || page >= data.pages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>}
        </>}
      </Card.Body>
    </Card>
  );
};

export default CustomerActivities;
